import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import useCalculatorQuote from "@/hooks/useCalculatorQuote";
import { toast } from "sonner";
import {
    Info,
    CheckCircle2,
    AlertTriangle,
    Sparkles,
    Loader2,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Perx branded insurance premium calculator.
//
// Every number on this page comes from the server (see
// backend/routes/calculator.py): coverage choices go up, priced sections and
// discounted totals come back. Nothing is computed here, and no premium is
// ever sent — Apply recomputes server-side from the choices alone.
//
// The plan / deductible / cap / area / network strings are likewise served,
// not hardcoded: each one is a literal substring of the rate-table lookup key,
// so a copy edit in this file would silently break pricing.
// ---------------------------------------------------------------------------

// Stable per-section presentation. The option values themselves come from
// /member/calculator/options; only the testids and layout live here.
const SECTION_UI = {
    surgery: { testid: "cov-hs" },
    outpatient: { testid: "cov-op" },
    maternity: { testid: "cov-maternity" },
    dental: { testid: "cov-dental" },
};

const FREQUENCIES = [
    { label: "Quarterly", key: "quarterly" },
    { label: "Semi-Annual", key: "semi_annual" },
    { label: "Annual", key: "annual" },
];

function computeAge(dobIso) {
    if (!dobIso) return "";
    const dob = new Date(dobIso);
    if (Number.isNaN(dob.getTime())) return "";
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) age--;
    return age;
}

/**
 * Whether a plan is open to this member under the section's served
 * `plan_restrictions` (area, network, age). Mirrors the server's
 * premium_engine.surgery_plan_allowed, which rejects it regardless.
 */
function planAllowed(cfg, plan, member) {
    const rule = cfg.plan_restrictions?.[plan];
    if (!rule) return true;
    const age = computeAge(member.dob);
    return (
        rule.areas.includes(member.area) &&
        rule.networks.includes(member.network) &&
        typeof age === "number" &&
        age <= rule.max_age
    );
}

/** Deductible options for a plan, falling back to the flat list. */
const deductiblesFor = (cfg, plan) => cfg.deductibles_by_plan?.[plan] || cfg.deductibles;

const money = (n) =>
    Number(n || 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

/** Blank coverage state built from the server's option defaults. */
function coverageDefaults(options) {
    if (!options) return null;
    const sections = {};
    Object.entries(options.sections).forEach(([key, cfg]) => {
        sections[key] = { ...(cfg.defaults || { enabled: false }) };
    });
    const residences = options.residences || [];
    const preferred =
        residences.find((z) => z.residence === "Hong Kong") || residences[0];
    return {
        area: options.defaults.area,
        network: options.defaults.network,
        residence: preferred ? preferred.residence : "",
        sections,
    };
}

// The primary member's own mirror row in sub_users has relationship "self".
const isSelfSub = (sub) => sub.relationship === "self";

function buildMemberFromSub(sub, defaults) {
    return {
        id: sub.id,
        isPrimary: isSelfSub(sub),
        name: sub.name,
        gender: sub.gender || "",
        dob: sub.dob || "",
        ...defaults,
    };
}

export default function Calculator() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const dealIdParam = searchParams.get("deal");
    const { user } = useAuth();

    const [subs, setSubs] = useState([]);
    const [subsLoaded, setSubsLoaded] = useState(false);
    const seededSelf = useRef(false);
    const [options, setOptions] = useState(null);
    const [optionsError, setOptionsError] = useState(null);
    const [members, setMembers] = useState([]);
    const [activeIdx, setActiveIdx] = useState(0);
    const [frequency, setFrequency] = useState("Annual");
    const [busy, setBusy] = useState(false);
    const [isPaidUSD, setIsPaidUSD] = useState(false);

    const defaults = useMemo(() => coverageDefaults(options), [options]);

    useEffect(() => {
        api.get("/member/calculator/options")
            .then((r) => setOptions(r.data))
            .catch((e) => setOptionsError(formatApiError(e)));
    }, []);

    useEffect(() => {
        api.get("/member/sub-users")
            .then((r) => setSubs(r.data || []))
            .catch(() => setSubs([]))
            .finally(() => setSubsLoaded(true));
    }, []);

    useEffect(() => {
        if (!dealIdParam) return;
        api.get(`/public/deals/${dealIdParam}`)
            .then((r) => setIsPaidUSD(Boolean(r.data?.is_paid_USD)))
            .catch(() => setIsPaidUSD(false));
    }, [dealIdParam]);

    // Start with the primary's own mirror row ticked. Done once only, so a
    // member who unticks themselves isn't re-added on the next render.
    useEffect(() => {
        if (seededSelf.current || !defaults || !subsLoaded) return;
        seededSelf.current = true;
        const self = subs.find((x) => isSelfSub(x) && x.membership_active === true);
        if (self) setMembers([buildMemberFromSub(self, defaults)]);
    }, [subs, subsLoaded, defaults]);

    // A restricted plan the member no longer qualifies for (area, network or
    // age changed) falls back to the section's default plan and deductible.
    useEffect(() => {
        if (!options) return;
        setMembers((ms) => {
            let changed = false;
            const next = ms.map((m) => {
                let sections = m.sections;
                Object.entries(options.sections).forEach(([key, cfg]) => {
                    const state = sections?.[key];
                    if (!state?.plan || planAllowed(cfg, state.plan, m)) return;
                    const { plan, deductible } = cfg.defaults || {};
                    sections = { ...sections, [key]: { ...state, plan, deductible } };
                });
                if (sections === m.sections) return m;
                changed = true;
                return { ...m, sections };
            });
            return changed ? next : ms;
        });
    }, [members, options]);

    // Eligibility: an adult sub-user (including the primary's own "self" row)
    // counts only while their membership is active; a minor counts only while
    // the adult who provides for them is currently included in the calculator.
    // `membership_active` is resolved server-side, which the raw
    // `membershipInForce` seat flag cannot do for the self row.
    const eligibleAdults = useMemo(
        () =>
            subs
                .filter((s) => !s.is_dependant && s.membership_active === true)
                .sort((a, b) => Number(isSelfSub(b)) - Number(isSelfSub(a))),
        [subs]
    );
    const eligibleAdultIds = useMemo(
        () => new Set(eligibleAdults.map((s) => s.id)),
        [eligibleAdults]
    );
    const dependantsByProvider = useMemo(() => {
        const map = new Map();
        subs
            .filter(
                (s) =>
                    s.is_dependant &&
                    s.primary_provider_id &&
                    eligibleAdultIds.has(s.primary_provider_id)
            )
            .forEach((d) => {
                const list = map.get(d.primary_provider_id) || [];
                list.push(d);
                map.set(d.primary_provider_id, list);
            });
        return map;
    }, [subs, eligibleAdultIds]);
    const includedSubIds = useMemo(
        () => new Set(members.map((m) => m.id)),
        [members]
    );

    const currencyLabel = isPaidUSD ? "USD" : "HKD";
    const symbol = isPaidUSD ? "US$" : "$";

    // ---- Quote -----------------------------------------------------------
    const quotePayload = useMemo(
        () =>
            members.map((m) => ({
                key: m.id,
                name: m.name,
                dob: m.dob,
                gender: m.gender,
                area: m.area,
                network: m.network,
                residence: m.residence,
                sections: m.sections,
            })),
        [members]
    );
    const { quote, loading: quoting, error: quoteError } = useCalculatorQuote(quotePayload);

    const quoteByKey = useMemo(() => {
        const map = new Map();
        (quote?.members || []).forEach((m) => map.set(m.key, m));
        return map;
    }, [quote]);

    const totals = quote?.totals;
    const eligibleCount = quote?.eligible_member_keys?.length || 0;

    const active = members[activeIdx];
    const activeQuote = active ? quoteByKey.get(active.id) : null;

    const setActive = useCallback(
        (patch) =>
            setMembers((ms) =>
                ms.map((m, i) => (i === activeIdx ? { ...m, ...patch } : m))
            ),
        [activeIdx]
    );

    const setSection = useCallback(
        (section, patch) =>
            setMembers((ms) =>
                ms.map((m, i) =>
                    i === activeIdx
                        ? {
                              ...m,
                              sections: {
                                  ...m.sections,
                                  [section]: { ...m.sections[section], ...patch },
                              },
                          }
                        : m
                )
            ),
        [activeIdx]
    );

    // Checking a sub-user adds them to the calculation; unchecking removes
    // them. Unchecking an adult also drops any of their dependants currently
    // included, since a dependant can only be included while their provider is.
    const toggleSub = (sub) => {
        const isIncluded = includedSubIds.has(sub.id);
        setMembers((ms) => {
            if (isIncluded) {
                const cascadeIds = new Set([
                    sub.id,
                    ...(dependantsByProvider.get(sub.id) || []).map((d) => d.id),
                ]);
                return ms.filter((m) => !cascadeIds.has(m.id));
            }
            return [...ms, buildMemberFromSub(sub, defaults)];
        });
        setActiveIdx(isIncluded ? 0 : members.length);
    };

    const apply = async () => {
        setBusy(true);
        try {
            const r = await api.post("/member/calculator/apply", {
                members: quotePayload,
                deal_id: dealIdParam || null,
                frequency,
                sub_user_ids: Array.from(includedSubIds),
            });
            const ref = r.data?.display_id;
            if (dealIdParam) {
                toast.success(
                    `Application ${ref} submitted — the vendor will follow up with a firm quote.`
                );
                // `resume=calculator` tells the deal page to reopen the
                // purchase workflow past the calculator step (e.g. straight
                // to booking) instead of dropping the member back at a
                // fresh, unstarted page — see WorkflowRunner's `initialStep`.
                navigate(`/member/deals/${dealIdParam}?resume=calculator`);
            } else {
                toast.success(
                    `Application ${ref} submitted · ${currencyLabel} ${money(
                        r.data?.totals?.perx?.annual
                    )} a year`
                );
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    if (optionsError) {
        return (
            <>
                <PageHeader
                    eyebrow="Insurance"
                    title="Coverage selection"
                    testid="calculator-header"
                />
                <div className="px-6 lg:px-12 py-8">
                    <div className="rounded-2xl bg-white border border-red-200 p-6 text-sm text-navy-700">
                        <div className="font-semibold text-red-600 mb-1">
                            The calculator couldn&rsquo;t load.
                        </div>
                        {optionsError}
                    </div>
                </div>
            </>
        );
    }

    if (!options || !subsLoaded) {
        return (
            <>
                <PageHeader
                    eyebrow="Insurance"
                    title="Coverage selection"
                    testid="calculator-header"
                />
                <div className="px-6 lg:px-12 py-16 flex justify-center">
                    <Loader2 className="animate-spin text-navy-300" size={28} />
                </div>
            </>
        );
    }

    const freqValue = (key) => totals?.perx?.[key] ?? 0;

    return (
        <>
            <PageHeader
                eyebrow="Insurance"
                title="Coverage selection"
                description="Estimate your Perx-discounted premium across your household. Choose which eligible family members to include below."
                action={
                    <Button
                        onClick={apply}
                        disabled={busy || quoting || eligibleCount === 0}
                        data-testid="calc-apply"
                        className="rounded-full bg-navy-700 hover:bg-navy-800 text-white h-11 px-8 font-semibold tracking-widest disabled:opacity-50"
                    >
                        {busy ? "SENDING…" : "APPLY"}
                    </Button>
                }
                testid="calculator-header"
            />

            <div className="px-6 lg:px-12 py-8 space-y-6">
                {quoteError && (
                    <div className="rounded-2xl bg-white border border-red-200 p-4 text-sm text-red-600">
                        Couldn&rsquo;t reach the pricing service. {formatApiError(quoteError)}
                    </div>
                )}

                {/* ---------- Top summary strip ---------- */}
                <div className="grid lg:grid-cols-2 gap-4">
                    <div className="rounded-2xl bg-white shadow-soft p-5 border border-navy-100/60">
                        <SummaryRow
                            label="Total Market Rate"
                            value={totals?.market?.annual}
                            symbol={symbol}
                        />
                        <SummaryRow
                            label="Perx Discounted Premium"
                            value={totals?.discount_adjusted_annual}
                            tone="text-navy-700"
                            symbol={symbol}
                        />
                        <SummaryRow
                            label="Additional welcome discount"
                            value={totals?.welcome_discount_annual}
                            tone="text-navy-500"
                            negative
                            symbol={symbol}
                        />
                    </div>
                    <div className="rounded-2xl bg-gradient-to-br from-navy-700 to-navy-800 text-white shadow-soft p-6 relative">
                        {quoting && (
                            <Loader2
                                className="animate-spin absolute top-4 right-4 text-gold-500"
                                size={16}
                            />
                        )}
                        <div className="grid grid-cols-2 gap-6 items-center">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.3em] text-gold-500">
                                    Total Perx discounted premium
                                </div>
                                <div
                                    className="font-display text-4xl mt-2 leading-none"
                                    data-testid="calc-total-perx"
                                >
                                    {currencyLabel} {money(totals?.perx?.annual)}
                                </div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.3em] text-gold-500">
                                    You save with us
                                </div>
                                <div
                                    className="font-display text-3xl mt-2 leading-none text-emerald-300"
                                    data-testid="calc-savings"
                                >
                                    {currencyLabel} {money(totals?.savings_annual)}
                                </div>
                            </div>
                        </div>
                        <div className="mt-5 grid grid-cols-3 gap-2">
                            {FREQUENCIES.map((f) => {
                                const selected = frequency === f.label;
                                return (
                                    <button
                                        key={f.label}
                                        type="button"
                                        data-testid={`calc-freq-${f.label.toLowerCase()}`}
                                        onClick={() => setFrequency(f.label)}
                                        className={`rounded-lg p-3 text-left transition-colors ${
                                            selected
                                                ? "bg-gold-500 text-navy-900"
                                                : "bg-white/10 hover:bg-white/20 text-navy-50"
                                        }`}
                                    >
                                        <div className="text-[10px] uppercase tracking-widest opacity-80">
                                            {f.label}
                                        </div>
                                        <div className="font-display text-lg mt-1">
                                            {symbol}
                                            {money(freqValue(f.key))}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                        <div className="text-[10px] text-navy-200/70 mt-2">
                            Instalments carry a 4% loading semi-annually and 5% quarterly.
                        </div>
                    </div>
                </div>

                {/* ---------- Body — sidebar + form ---------- */}
                <div className="grid lg:grid-cols-[280px_1fr] gap-6 items-start">
                    {/* Left sidebar — enrolled members */}
                    <aside className="space-y-4">
                        <div className="rounded-2xl bg-cream border border-gold-200/60 p-4 flex gap-3">
                            <Info className="text-gold-600 shrink-0 mt-0.5" size={16} />
                            <p className="text-[11px] text-navy-700 leading-relaxed">
                                Adults with an active membership can be included below.
                                A child under 18 can only be included once the parent who
                                provides for them is included too.
                            </p>
                        </div>

                        {eligibleAdults.length > 0 && (
                            <div className="rounded-2xl bg-white border border-navy-100 p-4 space-y-2.5">
                                <div className="text-[10px] uppercase tracking-widest text-navy-500 mb-1">
                                    Eligible family members
                                </div>
                                {eligibleAdults.map((adult) => {
                                    const adultChecked = includedSubIds.has(adult.id);
                                    const dependants = dependantsByProvider.get(adult.id) || [];
                                    return (
                                        <div key={adult.id}>
                                            <label
                                                htmlFor={`calc-sub-${adult.id}`}
                                                className="flex items-center gap-2 text-sm text-navy-700 cursor-pointer"
                                            >
                                                <input
                                                    id={`calc-sub-${adult.id}`}
                                                    type="checkbox"
                                                    data-testid={`calc-sub-check-${adult.id}`}
                                                    checked={adultChecked}
                                                    onChange={() => toggleSub(adult)}
                                                    className="accent-navy-700 h-4 w-4"
                                                />
                                                <span>
                                                    {adult.name}{" "}
                                                    <span className="text-xs text-navy-400">
                                                        · {isSelfSub(adult) ? "You" : adult.relationship}
                                                    </span>
                                                </span>
                                            </label>
                                            {dependants.map((dep) => {
                                                const depChecked = includedSubIds.has(dep.id);
                                                return (
                                                    <label
                                                        key={dep.id}
                                                        htmlFor={`calc-sub-${dep.id}`}
                                                        className={`ml-6 flex items-center gap-2 text-xs mt-1.5 ${
                                                            adultChecked
                                                                ? "text-navy-600 cursor-pointer"
                                                                : "text-navy-300 cursor-not-allowed"
                                                        }`}
                                                    >
                                                        <input
                                                            id={`calc-sub-${dep.id}`}
                                                            type="checkbox"
                                                            data-testid={`calc-sub-check-${dep.id}`}
                                                            checked={depChecked}
                                                            disabled={!adultChecked}
                                                            onChange={() => toggleSub(dep)}
                                                            className="accent-navy-700 h-3.5 w-3.5"
                                                        />
                                                        <span>
                                                            {dep.name}{" "}
                                                            <span className="text-navy-300">
                                                                · {dep.relationship}
                                                            </span>
                                                        </span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {members.map((m, i) => {
                            const selected = activeIdx === i;
                            const q = quoteByKey.get(m.id);
                            const ready = Boolean(q?.eligible);
                            return (
                                <button
                                    key={m.id}
                                    type="button"
                                    onClick={() => setActiveIdx(i)}
                                    data-testid={`calc-member-tab-${i}`}
                                    className={`w-full text-left rounded-2xl p-4 border transition-all ${
                                        selected
                                            ? "bg-white border-gold-500 shadow-soft"
                                            : "bg-white/70 border-navy-100 hover:border-navy-300"
                                    }`}
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <div>
                                            <div className="font-display text-base text-navy-700 leading-tight">
                                                {m.name || `Member ${i + 1}`}
                                                {m.isPrimary && (
                                                    <span className="text-[10px] uppercase tracking-widest text-gold-600 ml-2 align-middle">
                                                        You
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-[11px] text-navy-500 mt-0.5">
                                                Age: {q?.age ?? computeAge(m.dob) ?? "—"} ·{" "}
                                                {(m.gender || "—").toLowerCase()}
                                            </div>
                                        </div>
                                        {ready ? (
                                            <CheckCircle2
                                                className="text-emerald-500 shrink-0"
                                                size={16}
                                                data-testid={`calc-member-ready-${i}`}
                                            />
                                        ) : (
                                            <AlertTriangle
                                                className="text-amber-500 shrink-0"
                                                size={16}
                                                data-testid={`calc-member-blocked-${i}`}
                                            />
                                        )}
                                    </div>
                                    <div className="text-[11px] text-navy-500 mt-3 space-y-0.5">
                                        <div>
                                            Total Market Rate:{" "}
                                            <span className="text-navy-700">
                                                {symbol}
                                                {money(q?.market_rate)}
                                            </span>
                                        </div>
                                        <div>
                                            Total Perx Premium:{" "}
                                            <span className="text-navy-700">
                                                {symbol}
                                                {money(q?.discount_adjusted_premium)}
                                            </span>
                                        </div>
                                        <div>
                                            Total Perx Adjusted Premium:{" "}
                                            <span className="text-emerald-700 font-semibold">
                                                {symbol}
                                                {money(q?.perx_adjusted_premium)}
                                            </span>
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </aside>

                    {/* Right — active member form */}
                    {!active ? (
                        <div
                            className="rounded-2xl bg-white border border-navy-100/60 shadow-soft p-10 text-center"
                            data-testid="calc-empty"
                        >
                            <div className="font-display text-xl text-navy-700">
                                No one selected yet
                            </div>
                            <p className="text-sm text-navy-500 mt-2">
                                {eligibleAdults.length > 0
                                    ? "Tick a family member on the left to start building a quote."
                                    : "No one on your account has an active membership, so there is no one to include."}
                            </p>
                        </div>
                    ) : (
                    <div className="space-y-6">
                        {/* Identity comes from the account record, not this page — a
                            quote whose DOB disagrees with the policy record is worthless.
                            Missing fields are named here with a link to fix them. */}
                        {activeQuote?.missing_basic?.length > 0 && (
                            <div
                                className="rounded-2xl bg-amber-50 border border-amber-200 p-4 flex gap-3 text-sm"
                                data-testid="calc-missing-basic"
                            >
                                <AlertTriangle
                                    className="text-amber-500 shrink-0 mt-0.5"
                                    size={16}
                                />
                                <div className="text-navy-700">
                                    <span className="font-semibold">
                                        {active.name || "This member"}
                                    </span>{" "}
                                    can&rsquo;t be quoted until{" "}
                                    {activeQuote.missing_basic.join(", ")}{" "}
                                    {activeQuote.missing_basic.length > 1 ? "are" : "is"} on
                                    file.{" "}
                                    <Link
                                        to={active.isPrimary ? "/member/profile" : "/member/family"}
                                        className="text-gold-600 underline underline-offset-2"
                                    >
                                        {active.isPrimary
                                            ? "Update your profile"
                                            : "Update family details"}
                                    </Link>
                                </div>
                            </div>
                        )}

                        {/* Basic information */}
                        <Card>
                            <SectionTitle>Basic information</SectionTitle>
                            <div className="grid sm:grid-cols-3 gap-4">
                                <FormField label="Age">
                                    <div
                                        className="h-10 flex items-center text-sm text-navy-700"
                                        data-testid="calc-age"
                                    >
                                        {activeQuote?.age ?? computeAge(active.dob) ?? "—"}
                                    </div>
                                </FormField>
                                <FormField label="Gender">
                                    <div
                                        className="h-10 flex items-center text-sm text-navy-700 capitalize"
                                        data-testid="calc-gender"
                                    >
                                        {active.gender || "—"}
                                    </div>
                                </FormField>
                                <FormField label="Date of Birth">
                                    <div
                                        className="h-10 flex items-center text-sm text-navy-700"
                                        data-testid="calc-dob"
                                    >
                                        {active.dob || "—"}
                                    </div>
                                </FormField>
                                <FormField label="Area" required>
                                    <Select
                                        value={active.area}
                                        onValueChange={(v) => setActive({ area: v })}
                                    >
                                        <SelectTrigger data-testid="calc-area">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {options.areas.map((o) => (
                                                <SelectItem key={o} value={o}>
                                                    {o}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormField>
                                <FormField label="Network" required>
                                    <Select
                                        value={active.network}
                                        onValueChange={(v) => setActive({ network: v })}
                                    >
                                        <SelectTrigger data-testid="calc-network">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {options.networks.map((o) => (
                                                <SelectItem key={o} value={o}>
                                                    {o}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormField>
                                <FormField label="Residence" required>
                                    <Select
                                        value={active.residence}
                                        onValueChange={(v) => setActive({ residence: v })}
                                    >
                                        <SelectTrigger data-testid="calc-residence">
                                            <SelectValue placeholder="Select residence" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {options.residences.map((z) => (
                                                <SelectItem key={z.residence} value={z.residence}>
                                                    {z.residence}
                                                    {z.discount_pct > 0 && (
                                                        <span className="text-navy-400">
                                                            {" "}
                                                            · −{z.discount_pct}%
                                                        </span>
                                                    )}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormField>
                            </div>
                        </Card>

                        {/* Coverage */}
                        <SectionTitle>Coverage</SectionTitle>
                        <div className="grid md:grid-cols-2 gap-4">
                            {options.section_order.map((key) => {
                                const cfg = options.sections[key];
                                // Maternity is female-only — the server enforces
                                // this independently, regardless of what is sent.
                                if (
                                    cfg.gender &&
                                    String(active.gender || "").toLowerCase() !== cfg.gender
                                ) {
                                    return null;
                                }
                                const state = active.sections[key] || {};
                                const sq = activeQuote?.sections?.[key];
                                const testid = SECTION_UI[key].testid;
                                const alwaysOn = Boolean(cfg.always_on);
                                return (
                                    <CoverageCard
                                        key={key}
                                        testid={testid}
                                        title={cfg.label}
                                        checked={alwaysOn ? true : Boolean(state.enabled)}
                                        locked={alwaysOn}
                                        onCheckedChange={(v) =>
                                            setSection(key, { enabled: v })
                                        }
                                    >
                                        <div className="grid grid-cols-2 gap-3">
                                            <FormField label="Plan" required small>
                                                <Select
                                                    value={state.plan || ""}
                                                    onValueChange={(v) => {
                                                        // Cap and deductible options are
                                                        // plan-dependent, so a plan change can
                                                        // orphan the chosen cap or deductible.
                                                        const patch = { plan: v };
                                                        if (cfg.caps_by_plan) {
                                                            const caps = cfg.caps_by_plan[v] || [];
                                                            if (!caps.includes(state.cap)) {
                                                                patch.cap = caps[0];
                                                            }
                                                        }
                                                        if (cfg.deductibles) {
                                                            const deds = deductiblesFor(cfg, v);
                                                            if (!deds.includes(state.deductible)) {
                                                                patch.deductible = deds[0];
                                                            }
                                                        }
                                                        setSection(key, patch);
                                                    }}
                                                >
                                                    <SelectTrigger data-testid={`${testid}-plan`}>
                                                        <SelectValue placeholder="Plan *" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {cfg.plans
                                                            .filter((o) =>
                                                                planAllowed(cfg, o, active)
                                                            )
                                                            .map((o) => (
                                                            <SelectItem key={o} value={o}>
                                                                {o}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </FormField>

                                            {cfg.deductibles && (
                                                <FormField label="Deductible" required small>
                                                    <Select
                                                        value={state.deductible || ""}
                                                        onValueChange={(v) =>
                                                            setSection(key, { deductible: v })
                                                        }
                                                    >
                                                        <SelectTrigger
                                                            data-testid={`${testid}-deductible`}
                                                        >
                                                            <SelectValue placeholder="Deductible *" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {deductiblesFor(cfg, state.plan).map((o) => (
                                                                <SelectItem key={o} value={o}>
                                                                    {o}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </FormField>
                                            )}

                                            {cfg.caps_by_plan && (
                                                <FormField label="Cap" required small>
                                                    <Select
                                                        value={state.cap || ""}
                                                        onValueChange={(v) =>
                                                            setSection(key, { cap: v })
                                                        }
                                                    >
                                                        <SelectTrigger data-testid={`${testid}-cap`}>
                                                            <SelectValue placeholder="Cap *" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {(
                                                                cfg.caps_by_plan[state.plan] || []
                                                            ).map((o) => (
                                                                <SelectItem key={o} value={o}>
                                                                    {o}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </FormField>
                                            )}
                                        </div>
                                        <PremiumPill
                                            status={sq?.status}
                                            value={sq?.premium}
                                            symbol={symbol}
                                            testid={`${testid}-premium`}
                                        />
                                    </CoverageCard>
                                );
                            })}
                        </div>

                        <SelectionSummary
                            member={active}
                            options={options}
                        />

                        <div className="rounded-xl bg-navy-700/5 border border-navy-100 p-4 text-[11px] text-navy-500 flex gap-2 items-start">
                            <Sparkles className="text-gold-600 shrink-0 mt-0.5" size={14} />
                            <span>
                                Premiums come from the current rate table and refresh as you
                                change coverage. When you tap{" "}
                                <span className="text-navy-700 font-semibold">APPLY</span>, an
                                application is raised for everyone marked ready, and the
                                figures are recalculated at submission.
                            </span>
                        </div>
                    </div>
                    )}
                </div>
            </div>
        </>
    );
}

/* ------------------------------ atoms ------------------------------ */

function SummaryRow({ label, value, tone = "text-navy-700", negative, symbol = "$" }) {
    const shown = Math.abs(Number(value || 0)).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    return (
        <div className="flex items-center justify-between border-b last:border-b-0 border-navy-50 py-2.5">
            <div className="text-xs text-navy-500 uppercase tracking-widest">
                {label}
            </div>
            <div className={`font-display text-lg ${tone}`}>
                {negative && Number(value || 0) !== 0 ? "-" : ""}{symbol}{shown}
            </div>
        </div>
    );
}

function Card({ children }) {
    return (
        <div className="rounded-2xl bg-white shadow-soft p-6 border border-navy-100/60">
            {children}
        </div>
    );
}
function SectionTitle({ children }) {
    return (
        <h2 className="font-display text-xl text-navy-700 mb-4">{children}</h2>
    );
}
function FormField({ label, required, small, children }) {
    return (
        <div>
            <Label
                className={`text-navy-700 uppercase tracking-widest ${small ? "text-[10px]" : "text-xs"}`}
            >
                {label}
                {required && <span className="text-gold-600 ml-1">*</span>}
            </Label>
            <div className="mt-1.5">{children}</div>
        </div>
    );
}
function CoverageCard({
    title,
    checked,
    onCheckedChange,
    children,
    testid,
    locked,
    className = "",
}) {
    return (
        <div
            data-testid={testid}
            className={`rounded-2xl bg-white shadow-soft border p-5 space-y-4 ${
                checked ? "border-gold-400" : "border-navy-100"
            } ${className}`}
        >
            <label
                className={`flex items-center gap-3 ${locked ? "" : "cursor-pointer"}`}
            >
                <Checkbox
                    checked={checked}
                    disabled={locked}
                    onCheckedChange={(v) => onCheckedChange(Boolean(v))}
                    data-testid={`${testid}-check`}
                    className="data-[state=checked]:bg-navy-700 data-[state=checked]:border-navy-700"
                />
                <span className="font-display text-lg text-navy-700">{title}</span>
                {locked && (
                    <span className="text-[10px] uppercase tracking-widest text-navy-400">
                        Always included
                    </span>
                )}
            </label>
            <div className={checked ? "" : "opacity-50 pointer-events-none"}>
                {children}
            </div>
        </div>
    );
}

/**
 * Plain-language read-back of what the active member has actually chosen.
 *
 * Rendered from the member's own selections rather than the server's quote
 * echo, so it moves with the dropdown instead of trailing the debounced
 * round-trip — and it survives a pricing outage, which nulls the quote.
 *
 * The sentences come from the server (`section.descriptions`, ultimately
 * premium_engine.COVERAGE_DESCRIPTIONS) keyed by the option strings
 * themselves. A key with no copy — or an older API that predates the field —
 * simply renders the row without its sentence, never an empty panel.
 *
 * Deliberately carries no premiums and no pricing status: those belong to
 * PremiumPill, inside the card the member was just looking at.
 */
function SelectionSummary({ member, options }) {
    const rows = options.section_order.flatMap((key) => {
        const cfg = options.sections[key];
        // Mirror the coverage cards exactly: a section the member cannot
        // choose must never be narrated back to them.
        if (cfg.gender && String(member.gender || "").toLowerCase() !== cfg.gender) {
            return [];
        }
        const state = member.sections[key] || {};
        if (!cfg.always_on && !state.enabled) return [];

        const copy = cfg.descriptions || {};
        const parts = [];
        const notes = [];
        if (state.plan) {
            parts.push(state.plan);
            if (copy.plans?.[state.plan]) notes.push(copy.plans[state.plan]);
        }
        if (cfg.deductibles && state.deductible) {
            parts.push(`${state.deductible} deductible`);
            if (copy.deductibles?.[state.deductible]) {
                notes.push(copy.deductibles[state.deductible]);
            }
        }
        if (cfg.caps_by_plan && state.cap) {
            // "Nil-7k Cap" already says cap; "20%" doesn't.
            parts.push(/cap/i.test(state.cap) ? state.cap : `${state.cap} cap`);
            if (copy.caps?.[state.cap]) notes.push(copy.caps[state.cap]);
        }

        return [{ key, label: cfg.label, value: parts.join(" · "), note: notes.join(" ") }];
    });

    if (!rows.length) return null;

    const who = member.isPrimary
        ? "What you\u2019ve selected"
        : `What ${member.name || "this member"} has selected`;

    return (
        <Card>
            <SectionTitle>{who}</SectionTitle>
            <div data-testid="calc-selection">
                {rows.map((r) => (
                    <div
                        key={r.key}
                        data-testid={`calc-selection-${r.key}`}
                        className="border-b last:border-b-0 border-navy-50 py-3 first:pt-0 last:pb-0"
                    >
                        <div className="flex flex-wrap items-baseline gap-x-2">
                            <span className="text-[10px] uppercase tracking-widest text-navy-500">
                                {r.label}
                            </span>
                            <span className="text-sm text-navy-700">{r.value}</span>
                        </div>
                        {r.note && (
                            <p className="text-[11px] text-navy-500 leading-relaxed mt-1">
                                {r.note}
                            </p>
                        )}
                    </div>
                ))}
            </div>
        </Card>
    );
}

/**
 * A section's price. There is deliberately no fallback number: when the rate
 * table has no row for the chosen combination the member is told so, rather
 * than being shown an invented figure they could then submit.
 */
function PremiumPill({ status, value, symbol = "$", testid }) {
    const unpriced = status === "unpriced";
    const priced = status === "priced";
    return (
        <div
            className={`rounded-lg p-3 mt-3 border ${
                unpriced ? "bg-amber-50 border-amber-200" : "bg-cream border-gold-200/60"
            }`}
            data-testid={testid}
        >
            <div className="text-[10px] uppercase tracking-widest text-navy-500">
                {unpriced ? "Not available" : "Premium"}
            </div>
            <div
                className={`font-display mt-0.5 ${
                    unpriced ? "text-sm text-amber-700 pt-1" : "text-xl text-navy-700"
                }`}
            >
                {priced ? (
                    <>
                        {symbol}
                        {money(value)}
                    </>
                ) : unpriced ? (
                    "No published rate for this combination"
                ) : (
                    "—"
                )}
            </div>
        </div>
    );
}
